import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody
} from "@/components/ui/table";
import { Plan, Charge, Discount } from './types/planTypes';
import PlanRow from './PlanRow';
import ChargeRow from './ChargeRow';
import DiscountRow from './DiscountRow';
import PlansTableHeader from './PlansTableHeader';
import ChargeMappingDialog from './ChargeMappingDialog';
import DiscountMappingDialog from './DiscountMappingDialog';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '@/hooks/useAppSelector';
import useGraphQLHelper from '@/utils/graphQLHelper';

interface PlansTableProps {
  // Optional plans - GraphQLHelper can provide these if not supplied
  plans?: Plan[];
  
  // Required parameters
  quarterlyVersions: string[];
  openImageModal: (version: string, planName: string | null) => void;
  isLegacyPlan: (planId: string) => boolean;
  showCurrentOnly: boolean;
  
  // Optional configuration
  openDeletePlanDialog?: (plan: Plan) => void;
  subslug?: string | null;
  company?: any;
  activePageId?: string | null;
  
  // All available scrapes for finding the true latest version
  allScrapes?: Array<{version: string, quarter: string}> | null;
}

const PlansTable: React.FC<PlansTableProps> = ({
  plans: propPlans,
  quarterlyVersions,
  openImageModal,
  openDeletePlanDialog,
  isLegacyPlan,
  showCurrentOnly,
  subslug,
  company,
  activePageId,
  allScrapes
}) => {
  // Get company slug from route params
  const { slug } = useParams<{ slug: string }>();
  
  // Get GraphQL helper functions
  const {
    isPlanInVersion,
    isChargeInVersion,
    isDiscountInVersion,
    isScreenshotInvalid,
    getUniqueChargesForPlan,
    getUniqueDiscountsForPlan,
    graphQLData,
    getAllPlans
  } = useGraphQLHelper();
  
  // Data sources from Redux
  const companyFromRedux = useAppSelector(state => state.company.company);
  
  // Use GraphQL data as primary source with fallbacks
  const effectiveCompany = useMemo(() => {
    return company || graphQLData || companyFromRedux;
  }, [company, graphQLData, companyFromRedux]);
  
  // Get pages from the most reliable data source
  const pages = useMemo(() => {
    return graphQLData?.pages || effectiveCompany?.pages || [];
  }, [graphQLData, effectiveCompany]);
  
  // Combine plans from props and GraphQL if available
  const plans = useMemo(() => {
    if (propPlans && propPlans.length > 0) return propPlans;
    return getAllPlans();
  }, [propPlans, getAllPlans]);
  
  // Find the page ID from company data - memoized to prevent recalculation
  const pageId = useMemo(() => {
    if (!pages || pages.length === 0) return null;
    
    // If there's a subslug, use it to find the specific page
    if (subslug) {
      const page = pages.find((p: any) => p.subslug === subslug || p.slug === subslug);
      if (page?.id) return page.id;
    }
    
    // Otherwise, use the first page's ID
    if (pages.length > 0) {
      return pages[0].id || pages[0].page_id || null;
    }
    
    return null;
  }, [pages, subslug]);
  
  // Use the most specific pageId available
  const resolvedPageId = useMemo(() => {
    // Use the activePageId from props if available
    if (activePageId) return activePageId;
    
    // Use the pageId from our memoized function
    if (pageId) return pageId;
    
    // Check global variables as last resort
    return (window as any).GLOBAL_PAGE_ID || 
           (window as any).tempPageId || 
           (window as any).pageId || 
           (window as any).activePageId || 
           (window as any).companyPageId || 
           null;
  }, [activePageId, pageId]);
  
  // Dialog state management
  const [dialogState, setDialogState] = useState({
    // Charge mapping dialog
    chargeMappingOpen: false,
    selectedCharge: null as Charge | null,
    
    // Discount mapping dialog
    discountMappingOpen: false,
    selectedDiscount: null as Discount | null,
    
    // Shared properties
    selectedVersion: null as string | null,
    selectedPlanId: null as string | null,
    selectedPlanName: null as string | null
  });
  
  // Refresh key to force re-render
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Listen for charge mapping updates via custom event
  useEffect(() => {
    // Handler for the custom event
    const handleChargeMappingUpdate = (e: CustomEvent) => {
      console.log('🔄 [PlansTable] Detected charge mapping update, refreshing table', e.detail);
      // Force component to re-render by updating the refresh key
      setRefreshKey(prevKey => prevKey + 1);
      
      // Update the dialog state to ensure proper refresh when a plan is active
      if (e.detail && e.detail.chargeId && e.detail.planId) {
        // Force refresh of the plan charges by updating the state
        setDialogState(prev => ({
          ...prev,
          // Make sure we retain existing charge mapping dialog state but force refresh
          refreshTimestamp: Date.now()
        }));
      }
    };
    
    // Add event listener
    window.addEventListener('charge-mapping-updated', handleChargeMappingUpdate);
    
    // Clean up
    return () => {
      window.removeEventListener('charge-mapping-updated', handleChargeMappingUpdate);
    };
  }, []);
  
  // Function to open the discount mapping dialog
  const openDiscountMappingDialog = (discount: Discount, planId: string, planName: string, version: string) => {
    // Ensure we don't try to map without a pageId
    if (!resolvedPageId) {
      console.error('Cannot open discount mapping dialog: No page ID found');
      return;
    }
    
    console.log(`[DiscountMapping] Opening dialog for discount ${discount.id} on plan ${planId}, page ${resolvedPageId}, version ${version}`);
    
    // Update the dialog state
    setDialogState(prev => ({
      ...prev,
      discountMappingOpen: true,
      selectedDiscount: discount,
      selectedPlanId: planId,
      selectedPlanName: planName,
      selectedVersion: version
    }));
  };
  
  // Function to open the charge mapping dialog
  const openChargeMappingDialog = (charge: Charge, planId: string, planName: string, version: string) => {
    // Ensure we don't try to map without a pageId
    if (!resolvedPageId) {
      console.error('Cannot open charge mapping dialog: No page ID found');
      return;
    }
    
    console.log(`[ChargeMapping] Opening dialog for charge ${charge.id} on plan ${planId}, page ${resolvedPageId}, version ${version}`);
    
    // Update the dialog state
    setDialogState(prev => ({
      ...prev,
      chargeMappingOpen: true,
      selectedCharge: charge,
      selectedPlanId: planId,
      selectedPlanName: planName,
      selectedVersion: version
    }));
  };
  
  // Function to call when the mapping is updated to refresh the view
  const handleMappingUpdated = () => {
    // Reset dialog state
    setDialogState(prev => ({
      ...prev,
      chargeMappingOpen: false,
      discountMappingOpen: false,
      selectedCharge: null,
      selectedDiscount: null
    }));
  };
  
  return (
    <div className="overflow-x-auto">
      <Table className="border">
        <PlansTableHeader 
          quarterlyVersions={quarterlyVersions}
          isScreenshotInvalid={isScreenshotInvalid}
          openImageModal={openImageModal}
          allScrapes={allScrapes}
        />
        <TableBody>
          {plans.map(plan => {
            const uniqueCharges = getUniqueChargesForPlan(plan.id);
            const uniqueDiscounts = getUniqueDiscountsForPlan ? getUniqueDiscountsForPlan(plan.id) : plan.discounts || [];
            return (
              <React.Fragment key={plan.id}>
                {/* Plan row */}
                <PlanRow 
                  plan={plan}
                  quarterlyVersions={quarterlyVersions}
                  isPlanInVersion={isPlanInVersion}
                  isScreenshotInvalid={isScreenshotInvalid}
                  openImageModal={openImageModal}
                  isLegacyPlan={isLegacyPlan}
                  showCurrentOnly={showCurrentOnly}
                  openDeleteDialog={openDeletePlanDialog}
                />

                {/* Charge rows */}
                {uniqueCharges.map(charge => (
                  <ChargeRow
                    key={charge.id}
                    charge={charge}
                    planId={plan.id}
                    planName={plan.display_name || plan.plan_name}
                    quarterlyVersions={quarterlyVersions}
                    isChargeInVersion={isChargeInVersion}
                    isScreenshotInvalid={isScreenshotInvalid}
                    openImageModal={openImageModal}
                    isLegacyPlan={isLegacyPlan}
                    showCurrentOnly={showCurrentOnly}
                    openChargeMappingDialog={openChargeMappingDialog}
                    onChargeDeleted={handleMappingUpdated}
                  />
                ))}
                
                {/* Discount rows */}
                {uniqueDiscounts.map(discount => (
                  <DiscountRow
                    key={discount.id}
                    discount={discount}
                    planId={plan.id}
                    planName={plan.display_name || plan.plan_name}
                    quarterlyVersions={quarterlyVersions}
                    isDiscountInVersion={isDiscountInVersion}
                    isScreenshotInvalid={isScreenshotInvalid}
                    openImageModal={openImageModal}
                    isLegacyPlan={isLegacyPlan}
                    showCurrentOnly={showCurrentOnly}
                    openDiscountMappingDialog={openDiscountMappingDialog}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      
      {/* Charge Mapping Dialog */}
      <ChargeMappingDialog
        open={dialogState.chargeMappingOpen}
        onOpenChange={(open) => setDialogState(prev => ({ ...prev, chargeMappingOpen: open }))}
        charge={dialogState.selectedCharge}
        planId={dialogState.selectedPlanId}
        planName={dialogState.selectedPlanName}
        version={dialogState.selectedVersion}
        pageId={resolvedPageId}
        slug={slug || null}
        isChargeInVersion={isChargeInVersion}
        onMappingUpdated={handleMappingUpdated}
      />
      
      {/* Discount Mapping Dialog */}
      <DiscountMappingDialog
        open={dialogState.discountMappingOpen}
        onOpenChange={(open) => setDialogState(prev => ({ ...prev, discountMappingOpen: open }))}
        discount={dialogState.selectedDiscount}
        planId={dialogState.selectedPlanId}
        planName={dialogState.selectedPlanName}
        version={dialogState.selectedVersion}
        pageId={resolvedPageId}
        slug={slug || null}
        isDiscountInVersion={isDiscountInVersion}
        onMappingUpdated={handleMappingUpdated}
      />
    </div>
  );
};

export default PlansTable;
